<template>
  <section id="mdsView">
    <Tabs>
      <TabsHeader tabs-header-class="atb-bg-white">
        <Tab
          v-for="(tab, index) in tabs"
          :key="'mdsTab' + tab.key"
          :index="index"
          :value="selectedTab"
          :closable="index !== 0"
          @tabClick="(tabIndex: number) => (selectedTab = tabIndex)"
          @close="(tabIndex: number) => closeTab(tabIndex)"
        >
          {{ tab.name }}
        </Tab>
      </TabsHeader>
      <TabsBody>
        <TabPanelItem
          v-for="(tab, index) in tabs"
          :key="tab.key"
        >
          <MdsList
            v-if="index === 0"
            v-show="selectedTab === index"
            :can-modify="canModify"
            @create="openNew"
            @copy="openCopy"
            @edit="openExisting"
          />
          <MdsDetails
            v-else
            v-show="selectedTab === index"
            :datasource="tab.datasource"
            :copy-from="tab.copyFrom"
            :read-only="!canModify"
            @saved="(saved: Datasource) => onSaved(index, saved)"
            @deleted="() => closeTab(index)"
          />
        </TabPanelItem>
      </TabsBody>
    </Tabs>
  </section>
</template>

<script lang="ts" setup>
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Tabs from '@atb-ui/eds-vue/src/components/Tabs/Tabs.vue';
  import Tab from '@atb-ui/eds-vue/src/components/Tabs/Tab.vue';
  import TabsHeader from '@atb-ui/eds-vue/src/components/Tabs/TabsHeader.vue';
  import TabsBody from '@atb-ui/eds-vue/src/components/Tabs/TabsBody.vue';
  import TabPanelItem from '@atb-ui/eds-vue/src/components/Tabs/TabPanelItem.vue';
  import AccessGuard from '@/app/shared/guards/access-guard';
  import { Datasource } from '@/app/shared/types/datasource.interface';
  import MdsList from '@/app/features/ams/tools/mds/components/MdsList.vue';
  import MdsDetails from '@/app/features/ams/tools/mds/components/MdsDetails.vue';

  /**
   * Tools > Multi Data Sources, laid out like 5.x: an "All Multi Data Sources" list tab,
   * and one closable tab per multi datasource being created or edited.
   */
  interface MdsTab {
    key: string;
    name: string;
    datasource: Datasource | null;
    copyFrom?: Datasource | null;
  }

  const { t } = useI18n();
  // Multi datasources are written through the admin API, which needs the client WRITE right.
  const canModify = AccessGuard.canModify('CLIENT');

  let newTabCount = 0;
  const tabs = ref<Array<MdsTab>>([
    { key: 'all', name: t('mdsAllTitle'), datasource: null },
  ]);
  const selectedTab = ref(0);

  const openNew = () => {
    newTabCount += 1;
    tabs.value.push({
      key: `new-${newTabCount}`,
      name: t('mdsNewTitle'),
      datasource: null,
    });
    selectedTab.value = tabs.value.length - 1;
  };

  const openCopy = (source: Datasource) => {
    newTabCount += 1;
    tabs.value.push({
      key: `new-${newTabCount}`,
      name: t('mdsNewTitle'),
      datasource: null,
      copyFrom: source,
    });
    selectedTab.value = tabs.value.length - 1;
  };

  const openExisting = (datasource: Datasource) => {
    const open = tabs.value.findIndex(
      (tab) => tab.datasource?.id === datasource.id
    );
    if (open > 0) {
      selectedTab.value = open;
      return;
    }
    tabs.value.push({
      key: `ds-${datasource.id}`,
      name: datasource.name,
      datasource,
    });
    selectedTab.value = tabs.value.length - 1;
  };

  // A new multi datasource keeps its tab after the first save; the tab takes its name.
  const onSaved = (index: number, saved: Datasource) => {
    const tab = tabs.value[index];
    if (tab) {
      tab.name = saved.name;
      tab.datasource = saved;
    }
  };

  const closeTab = (index: number) => {
    if (index === 0) {
      return;
    }
    tabs.value.splice(index, 1);
    if (selectedTab.value >= index) {
      selectedTab.value = Math.max(0, selectedTab.value - 1);
    }
  };

  defineExpose({ tabs, selectedTab });
</script>
